// Toast queue + host. Pattern per frontend-design §6 Toast / notification:
//   - bottom-right; max 4 visible
//   - success: 5s auto-dismiss, polite live region
//   - error: manual dismiss only, assertive live region
//   - info: 5s, polite (used for role-change-redirect notice)

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastSeverity = 'success' | 'error' | 'info';

export interface ToastInput {
  severity: ToastSeverity;
  message: string;
  // Optional action affordance (rare in V1 — frontend-design §6 limits
  // to one action per toast).
  actionLabel?: string;
  onAction?: () => void;
  // Override default auto-dismiss.
  dismissAfterMs?: number;
}

export interface Toast extends ToastInput {
  id: number;
}

interface ToastContextValue {
  show: (input: ToastInput) => void;
  dismiss: (id: number) => void;
}

const Context = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE = 4;
const DEFAULT_AUTO_DISMISS_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextIdRef = useRef(1);
  const timersRef = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (input: ToastInput) => {
      const id = nextIdRef.current++;
      setToasts((prev) => {
        const next = [...prev, { ...input, id }];
        // Drop the oldest when over the cap.
        if (next.length > MAX_VISIBLE) {
          const dropped = next.shift();
          if (dropped) {
            const t = timersRef.current.get(dropped.id);
            if (t !== undefined) {
              window.clearTimeout(t);
              timersRef.current.delete(dropped.id);
            }
          }
        }
        return next;
      });
      // Errors do not auto-dismiss.
      if (input.severity !== 'error') {
        const ms = input.dismissAfterMs ?? DEFAULT_AUTO_DISMISS_MS;
        const handle = window.setTimeout(() => dismiss(id), ms);
        timersRef.current.set(id, handle);
      }
    },
    [dismiss],
  );

  // Cleanup any pending timers on unmount.
  useEffect(() => {
    const map = timersRef.current;
    return () => {
      for (const t of map.values()) window.clearTimeout(t);
      map.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <Context.Provider value={value}>
      {children}
      <ToastHost toasts={toasts} onDismiss={dismiss} />
    </Context.Provider>
  );
}

export function useToast(): ToastContextValue {
  const v = useContext(Context);
  if (!v) throw new Error('useToast used outside <ToastProvider>');
  return v;
}

function ToastHost({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  const polite = toasts.filter((t) => t.severity !== 'error');
  const assertive = toasts.filter((t) => t.severity === 'error');
  return (
    <div className="toast-host" aria-label="Notifications">
      <div className="toast-stack" role="region">
        <div aria-live="polite" aria-relevant="additions text" className="visually-hidden">
          {polite.map((t) => (
            <span key={t.id}>{t.message}</span>
          ))}
        </div>
        <div aria-live="assertive" aria-relevant="additions text" className="visually-hidden">
          {assertive.map((t) => (
            <span key={t.id}>{t.message}</span>
          ))}
        </div>
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast-${t.severity}`}
            role={t.severity === 'error' ? 'alert' : 'status'}
          >
            <div className="toast-message">{t.message}</div>
            {t.actionLabel ? (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  t.onAction?.();
                  onDismiss(t.id);
                }}
              >
                {t.actionLabel}
              </button>
            ) : null}
            <button
              type="button"
              className="toast-dismiss"
              aria-label="Dismiss notification"
              onClick={() => onDismiss(t.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
